import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  LogFilters,
  LOG_LEVELS,
  DATE_PRESETS,
  DEFAULT_FILTERS,
  EVENT_TYPES,
  AUDIT_OUTCOMES,
  LogLevel,
  DatePreset,
  LogEventType,
  AuditOutcome,
  SortField,
} from '../types'
import {
  fetchEntityTypes,
  fetchStates,
  fetchSubsystems,
  fetchUsers,
} from '../services/logService'
import { formatSubsystem } from '../../../utils/utils'
import useTranslate from '../useTranslate'
import FilterCombobox from './FilterCombobox'
import useStyles from '../styles'

const SORT_FIELD_LABELS: Record<SortField, string> = {
  timestamp: 'Timestamp',
  level: 'Level',
  source: 'Source',
  machine: 'Machine',
  connection: 'Connection',
  state: 'State',
  user: 'User',
  message: 'Message',
}

function sortButtonLabel(field: SortField, order: 'newest' | 'oldest'): string {
  if (field === 'timestamp') {
    return order === 'newest' ? 'Newest first' : 'Oldest first'
  }
  const direction = order === 'newest' ? 'Z→A' : 'A→Z'
  return `${SORT_FIELD_LABELS[field]}: ${direction}`
}

function dateChipLabel(filters: LogFilters): string | null {
  if (filters.datePreset === 'custom') {
    const fmt = (v: string): string => v.replace('T', ' ')
    const from = filters.dateFrom
    const to = filters.dateTo
    const hasFrom = from != null && from !== ''
    const hasTo = to != null && to !== ''
    if (hasFrom && hasTo) return `Date: ${fmt(from)} → ${fmt(to)}`
    if (hasFrom) return `Date: from ${fmt(from)}`
    if (hasTo) return `Date: until ${fmt(to)}`
    return 'Date: Custom Range'
  }
  if (filters.datePreset === DEFAULT_FILTERS.datePreset) return null
  const preset = DATE_PRESETS.find(p => p.value === filters.datePreset)
  return `Date: ${preset?.label ?? filters.datePreset}`
}

interface LogToolbarProps {
  filters: LogFilters
  onFilterChange: (update: Partial<LogFilters>) => void
  onClearFilters: () => void
}

const LogToolbar: React.FC<LogToolbarProps> = ({
  filters,
  onFilterChange,
  onClearFilters,
}) => {
  const classes = useStyles()
  const t = useTranslate()

  // Reference data for the dropdowns: fetched once, cached for the session,
  // shared across remounts. A failed fetch just leaves the list empty — the
  // dropdowns degrade to free-text / "all" without blocking the toolbar.
  // Explicit result types: under this repo's TypeScript version the useQuery
  // generics don't infer from queryFn and `data` degrades to `any`.
  const subsystemsQuery = useQuery({
    queryKey: ['log-subsystems'],
    queryFn: fetchSubsystems,
    staleTime: Infinity,
  })
  const subsystemOptions: string[] = subsystemsQuery.data ?? []
  const usersQuery = useQuery({
    queryKey: ['log-users'],
    queryFn: fetchUsers,
    staleTime: Infinity,
  })
  const userOptions: string[] = usersQuery.data ?? []
  const statesQuery = useQuery({
    queryKey: ['log-states'],
    queryFn: fetchStates,
    staleTime: Infinity,
  })
  const stateOptions: string[] = statesQuery.data ?? []
  const entityTypesQuery = useQuery({
    queryKey: ['log-entity-types'],
    queryFn: fetchEntityTypes,
    staleTime: Infinity,
  })
  const entityTypeOptions: string[] = entityTypesQuery.data ?? []

  const removeUser = (value: string): void => {
    onFilterChange({ users: filters.users.filter(u => u !== value) })
  }

  const toggleUser = (value: string): void => {
    if (value === '') return
    if (filters.users.includes(value)) removeUser(value)
    else onFilterChange({ users: [...filters.users, value] })
  }

  const removeSubsystem = (value: string): void => {
    onFilterChange({
      subsystems: filters.subsystems.filter(s => s !== value),
    })
  }

  const toggleSubsystem = (value: string): void => {
    if (value === '') return
    if (filters.subsystems.includes(value)) removeSubsystem(value)
    else onFilterChange({ subsystems: [...filters.subsystems, value] })
  }

  const removeState = (value: string): void => {
    onFilterChange({ states: filters.states.filter(s => s !== value) })
  }

  const toggleState = (value: string): void => {
    if (value === '') return
    if (filters.states.includes(value)) removeState(value)
    else onFilterChange({ states: [...filters.states, value] })
  }

  const removeEntityType = (value: string): void => {
    onFilterChange({
      entityTypes: filters.entityTypes.filter(e => e !== value),
    })
  }

  const toggleEntityType = (value: string): void => {
    if (value === '') return
    if (filters.entityTypes.includes(value)) removeEntityType(value)
    else onFilterChange({ entityTypes: [...filters.entityTypes, value] })
  }

  const removeOutcome = (value: AuditOutcome): void => {
    onFilterChange({
      outcomes: filters.outcomes.filter(o => o !== value),
    })
  }

  const toggleOutcome = (value: AuditOutcome): void => {
    if (filters.outcomes.includes(value)) removeOutcome(value)
    else onFilterChange({ outcomes: [...filters.outcomes, value] })
  }

  const removeEventType = (value: LogEventType): void => {
    onFilterChange({
      eventTypes: filters.eventTypes.filter(t => t !== value),
    })
  }

  const toggleEventType = (value: LogEventType): void => {
    if (filters.eventTypes.includes(value)) removeEventType(value)
    else onFilterChange({ eventTypes: [...filters.eventTypes, value] })
  }

  const [chipsEl, setChipsEl] = useState<HTMLDivElement | null>(null)

  const levelClasses = (level: LogLevel | 'ALL') => {
    const isActive = filters.level === level
    let cls = classes.levelToggle
    if (isActive) cls += ` ${classes.levelToggleActive}`
    if (!isActive && level === 'INFO') cls += ` ${classes.levelToggleInfo}`
    if (!isActive && level === 'ERROR') cls += ` ${classes.levelToggleError}`
    if (!isActive && level === 'WARN') cls += ` ${classes.levelToggleWarn}`
    return cls
  }

  return (
    <div className={classes.toolbar}>
      <div className={classes.toolbarRow}>
        <div className={classes.searchWrapper}>
          <span className={`material-symbols-outlined ${classes.searchIcon}`}>
            search
          </span>
          <input
            className={classes.searchInput}
            type='text'
            placeholder='Search message, source, or Trace ID...'
            value={filters.search}
            onChange={e => onFilterChange({ search: e.target.value })}
          />
          {filters.search.length > 0 && (
            <button
              type='button'
              className={classes.searchClearButton}
              onClick={() => onFilterChange({ search: '' })}
              aria-label='Clear search'
            >
              <span
                className={`material-symbols-outlined ${classes.searchClearIcon}`}
              >
                close
              </span>
            </button>
          )}
        </div>

        <div className={classes.levelToggles}>
          {(['ALL', ...LOG_LEVELS] as const).map(level => (
            <button
              key={level}
              className={levelClasses(level)}
              onClick={() => onFilterChange({ level })}
            >
              {level}
            </button>
          ))}
        </div>

        <button
          className={classes.sortButton}
          onClick={() =>
            onFilterChange({
              sortOrder: filters.sortOrder === 'newest' ? 'oldest' : 'newest',
            })
          }
        >
          <span
            className='material-symbols-outlined'
            style={{ fontSize: 16 }}
          >
            sort
          </span>
          <span>{sortButtonLabel(filters.sortField, filters.sortOrder)}</span>
        </button>
      </div>

      <div className={classes.filtersRow}>
        <div className={classes.filtersLabel}>
          <span
            className='material-symbols-outlined'
            style={{ fontSize: 14 }}
          >
            filter_list
          </span>
          {t('log_viewer.filters')}
        </div>
        <div className={classes.filterChips}>
          <FilterCombobox
            icon='dns'
            label={
              filters.subsystems.length > 0
                ? 'Source: Add Service...'
                : 'Source: All Services'
            }
            options={subsystemOptions.map(s => ({
              value: s,
              label: formatSubsystem(s),
            }))}
            selectedValues={filters.subsystems}
            onSelect={toggleSubsystem}
            searchPlaceholder='Search services...'
            emptyText='No services found.'
            chipsContainer={chipsEl}
            chipPrefix='Source: '
          />

          <FilterCombobox
            icon='calendar_month'
            label={`Date: ${
              DATE_PRESETS.find(p => p.value === filters.datePreset)?.label ??
              filters.datePreset
            }`}
            options={DATE_PRESETS.map(p => ({
              value: p.value,
              label: p.label,
            }))}
            selectedValues={[filters.datePreset]}
            onSelect={value =>
              onFilterChange({ datePreset: value as DatePreset })
            }
            searchPlaceholder='Search date ranges...'
            emptyText='No date ranges found.'
            single
          />

          <FilterCombobox
            icon='category'
            label={
              filters.eventTypes.length > 0
                ? 'Type: Add Event...'
                : 'Type: All Events'
            }
            options={EVENT_TYPES.map(t => ({
              value: t.value,
              label: t.label,
            }))}
            selectedValues={filters.eventTypes}
            onSelect={value => toggleEventType(value as LogEventType)}
            searchPlaceholder='Search event types...'
            emptyText='No event types found.'
            chipsContainer={chipsEl}
            chipPrefix='Type: '
          />

          <FilterCombobox
            icon='person'
            label={filters.users.length > 0 ? 'User: Add...' : 'User: All'}
            options={userOptions.map(u => ({ value: u, label: u }))}
            selectedValues={filters.users}
            onSelect={toggleUser}
            searchPlaceholder='Search users...'
            emptyText='No users found.'
            allowCustomValue
            chipsContainer={chipsEl}
            chipPrefix='User: '
          />

          <FilterCombobox
            icon='sync_alt'
            label={filters.states.length > 0 ? 'State: Add...' : 'State: All'}
            options={stateOptions.map(s => ({ value: s, label: s }))}
            selectedValues={filters.states}
            onSelect={toggleState}
            searchPlaceholder='Search states...'
            emptyText='No states found.'
            chipsContainer={chipsEl}
            chipPrefix='State: '
          />

          <FilterCombobox
            icon='category'
            label={
              filters.entityTypes.length > 0 ? 'Entity: Add...' : 'Entity: All'
            }
            options={entityTypeOptions.map(e => ({ value: e, label: e }))}
            selectedValues={filters.entityTypes}
            onSelect={toggleEntityType}
            searchPlaceholder='Search entity types...'
            emptyText='No entity types found.'
            chipsContainer={chipsEl}
            chipPrefix='Entity: '
          />

          <FilterCombobox
            icon='check_circle'
            label={
              filters.outcomes.length > 0 ? 'Result: Add...' : 'Result: All'
            }
            options={AUDIT_OUTCOMES.map(o => ({
              value: o.value,
              label: o.label,
            }))}
            selectedValues={filters.outcomes}
            onSelect={value => toggleOutcome(value as AuditOutcome)}
            searchPlaceholder='Search results...'
            emptyText='No results found.'
            chipsContainer={chipsEl}
            chipPrefix='Result: '
          />
        </div>
        <button
          className={classes.clearFiltersButton}
          onClick={onClearFilters}
        >
          <span
            className='material-symbols-outlined'
            style={{ fontSize: 14 }}
          >
            filter_list_off
          </span>
          {t('log_viewer.clear_filters')}
        </button>
      </div>
      <div
        className={classes.chipsRow}
        ref={setChipsEl}
      >
        {dateChipLabel(filters) != null && (
          <span className={classes.traceChip}>
            <span
              className='material-symbols-outlined'
              style={{ fontSize: 14 }}
            >
              calendar_month
            </span>
            <span
              className={classes.traceChipLabel}
              title={dateChipLabel(filters) ?? ''}
            >
              {dateChipLabel(filters)}
            </span>
            <button
              type='button'
              className={classes.traceChipClear}
              onClick={() =>
                onFilterChange({
                  datePreset: DEFAULT_FILTERS.datePreset,
                  dateFrom: null,
                  dateTo: null,
                })
              }
              aria-label='Reset date filter'
            >
              <span
                className='material-symbols-outlined'
                style={{ fontSize: 14 }}
              >
                close
              </span>
            </button>
          </span>
        )}
        {filters.traceId.length > 0 && (
          <span className={classes.traceChip}>
            <span
              className='material-symbols-outlined'
              style={{ fontSize: 14 }}
            >
              hub
            </span>
            <span
              className={classes.traceChipLabel}
              title={filters.traceId}
            >
              Trace: {filters.traceId}
            </span>
            <button
              type='button'
              className={classes.traceChipClear}
              onClick={() => onFilterChange({ traceId: '' })}
              aria-label='Clear trace filter'
            >
              <span
                className='material-symbols-outlined'
                style={{ fontSize: 14 }}
              >
                close
              </span>
            </button>
          </span>
        )}
        {filters.requestId.length > 0 && (
          <span className={classes.traceChip}>
            <span
              className='material-symbols-outlined'
              style={{ fontSize: 14 }}
            >
              hub
            </span>
            <span
              className={classes.traceChipLabel}
              title={filters.requestId}
            >
              Request: {filters.requestId}
            </span>
            <button
              type='button'
              className={classes.traceChipClear}
              onClick={() => onFilterChange({ requestId: '' })}
              aria-label='Clear request filter'
            >
              <span
                className='material-symbols-outlined'
                style={{ fontSize: 14 }}
              >
                close
              </span>
            </button>
          </span>
        )}
      </div>

      {filters.datePreset === 'custom' && (
        <div className={classes.dateRangeRow}>
          <span className={classes.dateRangeLabel}>Range:</span>
          <input
            className={classes.dateInput}
            type='datetime-local'
            value={filters.dateFrom ?? ''}
            onChange={e =>
              onFilterChange({
                dateFrom: e.target.value !== '' ? e.target.value : null,
              })
            }
          />
          <span className={classes.dateRangeSeparator}>to</span>
          <input
            className={classes.dateInput}
            type='datetime-local'
            value={filters.dateTo ?? ''}
            onChange={e =>
              onFilterChange({
                dateTo: e.target.value !== '' ? e.target.value : null,
              })
            }
          />
        </div>
      )}
    </div>
  )
}

export default LogToolbar
